import { useState } from 'react'
import { supabase } from '../lib/supabase'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'

export default function ReviewModal({ booking, user, onClose, onSubmit }) {
  const [rating, setRating] = useState(0)
  const [hovered, setHovered] = useState(0)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const isOwner = booking.owner_id === user.id
  const revieweeName = isOwner ? booking.hirer?.full_name : booking.owner?.full_name
  const revieweeId = isOwner ? booking.hirer_id : booking.owner_id

  const handleSubmit = async () => {
    if (rating === 0) { setError('Please select a star rating'); return }
    setSubmitting(true)
    setError('')
    const { error: err } = await supabase.from('reviews').insert({
      booking_id: booking.id,
      reviewer_id: user.id,
      reviewee_id: revieweeId,
      listing_id: booking.listing_id,
      rating,
      comment: comment.trim(),
      role: isOwner ? 'owner' : 'hirer',
    })
    if (err) { setError(err.message); setSubmitting(false); return }
    const { data: existingReviews } = await supabase
      .from('reviews')
      .select('rating')
      .eq('reviewee_id', revieweeId)
    if (existingReviews?.length) {
      const avg = existingReviews.reduce((sum, r) => sum + r.rating, 0) / existingReviews.length
      await supabase.from('profiles').update({
        rating: Math.round(avg * 10) / 10,
        review_count: existingReviews.length
      }).eq('id', revieweeId)
    }

    try {
      const { data: recipientEmail } = await supabase.rpc('get_user_email', { user_id: revieweeId })
      const reviewerProfile = isOwner ? booking.owner : booking.hirer
      const { data: { session } } = await supabase.auth.getSession()
      await fetch(`${BACKEND_URL}/notify-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          recipientEmail,
          recipientName: revieweeName,
          reviewerName: reviewerProfile?.full_name || 'Someone',
          rating,
          comment: comment.trim() || 'No comment left.',
          itemTitle: booking.listings?.title || 'your item',
        })
      })
    } catch (e) {
      console.log('Review notification failed silently:', e)
    }

    setSubmitting(false)
    onSubmit()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <h3 style={{ fontWeight: 600, marginBottom: 4 }}>Leave a review</h3>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>
          How was your experience with <strong>{revieweeName}</strong>?
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              onClick={() => setRating(star)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 40, color: star <= (hovered || rating) ? '#EF9F27' : '#D3D1C7', transition: 'color 0.1s' }}
            >
              ★
            </button>
          ))}
        </div>
        {rating > 0 && (
          <div style={{ textAlign: 'center', marginBottom: 16, fontSize: 14, color: 'var(--text-muted)' }}>
            {['', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent!'][rating]}
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Comment (optional)</label>
          <textarea
            className="form-control"
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Share your experience..."
            style={{ minHeight: 90 }}
          />
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose}>Skip</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || rating === 0}>
            {submitting ? 'Submitting...' : 'Submit review'}
          </button>
        </div>
      </div>
    </div>
  )
}